import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { isAxiosError } from "axios";
import { FormEventHandler, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router";

import AdminOrgApps from "@/admin/components/AdminOrgApps";
import { AdminOrgLinearCleanupIssues } from "@/admin/components/AdminOrgLinearCleanupIssues";
import AdminOrgMembers from "@/admin/components/AdminOrgMembers";
import AdminOrgPullRequests from "@/admin/components/AdminOrgPullRequests";
import AdminOrgSwitcher from "@/admin/components/AdminOrgSwitcher";
import { useAdminDemoAppCreateMutation } from "@/admin/data/useAdminDemoAppCreateMutation";
import { useAdminOrgDeleteMutation } from "@/admin/data/useAdminOrgDeleteMutation";
import { useAdminOrgPatchMutation } from "@/admin/data/useAdminOrgPatchMutation";
import { useAdminStripeBilling } from "@/admin/data/useAdminStripeBilling";
import { useAdminStripeBillingSyncMutation } from "@/admin/data/useAdminStripeBillingSyncMutation";
import { useCodeReferencesSyncMutation } from "@/admin/data/useCodeReferenceSyncsMutation";
import { useOrgsData } from "@/app/data/useOrgsData";
import { DateInput } from "@/common/components/DateInput";
import { useSlugParam } from "@/common/hooks/useParam";
import { InviteKeyDisplay } from "@/global-settings/components/Team";
import { getTrialStatusLabel } from "@/global-settings/components/TrialStatus";
import { UploadLogoButton } from "@/global-settings/components/UploadLogoButton";
import {
  AdminCreateDemoAppType,
  CreateAppScenarios,
} from "@reflag/shared/adminAPI";
import { AccessLevel, AccessLevels } from "@reflag/shared/organizationAPI";

export default function AdminOrg() {
  const { orgId } = useSlugParam("org");
  const { data: orgReq, isLoading } = useOrgsData();
  const orgs = orgReq?.data ?? [];
  const updateOrgMutation = useAdminOrgPatchMutation(orgId!);
  const toast = useToast();
  const codeReferencesSyncMutation = useCodeReferencesSyncMutation(orgId!, {
    onSuccess: () => {
      console.log("onSuccess");
      toast({
        title: "Code references job started",
        status: "success",
      });
    },
    onError: (error) => {
      console.log("onError", error);
      toast({
        title: `Error syncing code references: ${error}`,
        status: "error",
      });
    },
  });

  const createDemoAppDisclosure = useDisclosure();

  const org = orgs.find((o) => o.id === orgId);

  if (isLoading || !org) {
    return <Spinner />;
  }

  const handleAccessLevelChange = (level: AccessLevel) => {
    updateOrgMutation.mutate({ accessLevel: level });
  };

  const handleDataResidencyChange = (isGlobal: boolean) => {
    updateOrgMutation.mutate({ isGlobal });
  };

  const handleTrialEndsAtDateChange = (value: Date | null) => {
    if (value instanceof Date) {
      updateOrgMutation.mutate({ trialEndsAt: value.toISOString() });
    } else {
      updateOrgMutation.mutate({ trialEndsAt: null });
    }
  };

  const handleLogoChange = (logoUrl: string | null) => {
    updateOrgMutation.mutate({ logoUrl });
  };

  const handleOrgSuspensionToggle = () => {
    updateOrgMutation.mutate({
      suspendedAt: org.suspendedAt === null ? new Date().toISOString() : null,
    });
  };

  const handleCodeReferencesSync = () => {
    codeReferencesSyncMutation.mutate();
  };

  return (
    <Box marginInline="auto" maxW="container.xl" paddingInline={4}>
      <Flex alignItems="center" as="header" gap={8} paddingBlock={4}>
        <Heading as="h1">
          <RouterLink to="/admin">Reflag admin</RouterLink>
        </Heading>

        <Flex alignItems="center" as="nav" gap={4} marginInlineStart="auto">
          <Flex alignItems="center" gap={2}>
            <FormLabel htmlFor="admin-org-switcher" margin="0" padding="0">
              Organization
            </FormLabel>
            <AdminOrgSwitcher id="admin-org-switcher" orgId={orgId} size="sm" />
          </Flex>
        </Flex>
      </Flex>

      <Flex as="main" direction="column" gap={10}>
        <section>
          <Table>
            <colgroup>
              <col width="230" />
            </colgroup>
            <Tbody>
              <Tr>
                <Th>Organization</Th>
                <Td>
                  <Flex alignItems="center" gap={2}>
                    <Avatar
                      key={`${org.id}-${org.logoUrl}`}
                      name={org.name}
                      size="sm"
                      src={org.logoUrl ?? undefined}
                    />
                    <Text>{org.name}</Text>
                  </Flex>
                </Td>
                <Td>
                  <ButtonGroup>
                    <UploadLogoButton
                      orgId={org.id}
                      onChange={handleLogoChange}
                    >
                      {({ onClick }) => (
                        <Button variant="outline" onClick={onClick}>
                          Upload logo
                        </Button>
                      )}
                    </UploadLogoButton>
                    <Button
                      variant="outline"
                      onClick={() => handleLogoChange(null)}
                    >
                      Remove logo
                    </Button>

                    <Button
                      colorScheme="red"
                      onClick={handleOrgSuspensionToggle}
                    >
                      {org.suspendedAt === null ? "Suspend" : "Unsuspend"}
                    </Button>
                  </ButtonGroup>
                </Td>
              </Tr>
              <Tr>
                <Th>Created at</Th>
                <Td>{org.createdAt}</Td>
                <Td></Td>
              </Tr>
              <Tr>
                <Th>Access level</Th>
                <Td>
                  <Select
                    maxW={"fit-content"}
                    value={org.accessLevel}
                    onChange={(e) =>
                      handleAccessLevelChange(
                        e.currentTarget.value as AccessLevel,
                      )
                    }
                  >
                    {AccessLevels.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </Select>
                </Td>
                <Td></Td>
              </Tr>
              <Tr>
                <Th>Data residency</Th>
                <Td>
                  <Select
                    maxW="fit-content"
                    value={org.isGlobal ? "global" : "eu"}
                    onChange={(e) =>
                      handleDataResidencyChange(
                        e.currentTarget.value === "global",
                      )
                    }
                  >
                    <option value="eu">EU</option>
                    <option value="global">Global</option>
                  </Select>
                </Td>
                <Td></Td>
              </Tr>
              <Tr>
                <Th>Trial status</Th>
                <Td>
                  <Flex alignItems="center" gap={4}>
                    <ButtonGroup>
                      <DateInput
                        value={
                          org.trialEndsAt ? org.trialEndsAt.slice(0, 10) : ""
                        }
                        onChange={(e) =>
                          handleTrialEndsAtDateChange(
                            e.currentTarget.valueAsDate,
                          )
                        }
                      />
                      <Button
                        variant="outline"
                        onClick={() => handleTrialEndsAtDateChange(null)}
                      >
                        Clear
                      </Button>
                    </ButtonGroup>

                    {org.trialEndsAt && (
                      <Text>
                        {getTrialStatusLabel(new Date(org.trialEndsAt))}
                      </Text>
                    )}

                    {org.trialEndsAt ? (
                      <Badge colorScheme="orange">Trial</Badge>
                    ) : org.accessLevel != "starter" ? (
                      <Badge colorScheme="green">Customer</Badge>
                    ) : null}
                  </Flex>
                </Td>
                <Td></Td>
              </Tr>
              <Tr>
                <Th>Invite link</Th>
                <Td>
                  <InviteKeyDisplay
                    editable={false}
                    org={org}
                  ></InviteKeyDisplay>
                </Td>
                <Td></Td>
              </Tr>
              <Tr>
                <Th>Slack connection</Th>
                <Td>
                  {org.integrations.slack ? "Connected" : "Not connected"}
                </Td>
                <Td></Td>
              </Tr>
            </Tbody>
          </Table>
        </section>

        <AdminStripeBillingSection orgId={orgId!} />

        <Divider />

        <Flex direction="column" gap={4}>
          <Grid gridGap={4} templateColumns="1fr 1fr">
            <GridItem as="section">
              <Flex direction="column" gap={2}>
                <Heading as="h2" fontSize="2xl">
                  Apps
                </Heading>

                <AdminOrgApps org={org} />

                <Box>
                  <Button
                    variant="outline"
                    onClick={createDemoAppDisclosure.onOpen}
                  >
                    Create demo app
                  </Button>
                  <CreateDemoAppModal
                    disclosure={createDemoAppDisclosure}
                    orgId={orgId!}
                  />
                </Box>
              </Flex>
            </GridItem>

            <GridItem as="section">
              <Heading as="h2" fontSize="2xl">
                Members
              </Heading>

              <AdminOrgMembers org={org} />
            </GridItem>
          </Grid>

          <Divider />
          <Heading as="h2" fontSize="2xl">
            Pull Requests{" "}
            <Button
              isLoading={codeReferencesSyncMutation.isPending}
              variant="outline"
              onClick={handleCodeReferencesSync}
            >
              Sync code refs
            </Button>
          </Heading>
          <AdminOrgPullRequests org={org} />
        </Flex>
        <Flex direction="column" gap={4}>
          <Heading as="h2" fontSize="xl">
            Linear cleanup issues
          </Heading>
          <AdminOrgLinearCleanupIssues orgId={org.id} />
        </Flex>

        <Divider />

        <DeleteOrganizationSection orgId={org.id} orgName={org.name} />
      </Flex>
    </Box>
  );
}

function DeleteOrganizationSection({
  orgId,
  orgName,
}: {
  orgId: string;
  orgName: string;
}) {
  const disclosure = useDisclosure();
  const deleteMutation = useAdminOrgDeleteMutation(orgId);
  const navigate = useNavigate();
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [confirmation, setConfirmation] = useState("");

  const closeDialog = () => {
    disclosure.onClose();
    setConfirmation("");
  };

  const handleDelete = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        navigate("/admin");
        toast({
          title: "Organization deleted",
          description: `${orgName} has been permanently deleted.`,
          status: "success",
        });
      },
      onError: (error) => {
        toast({
          title: "Unable to delete organization",
          description: getAPIErrorMessage(error, "Unknown error"),
          status: "error",
        });
      },
    });
  };

  return (
    <Box as="section" paddingBottom={10}>
      <Heading as="h2" fontSize="2xl" marginBottom={2}>
        Danger zone
      </Heading>
      <Text marginBottom={4}>
        Permanently delete this organization and all of its Reflag data.
      </Text>
      <Button colorScheme="red" onClick={disclosure.onOpen}>
        Delete organization
      </Button>

      <AlertDialog
        closeOnEsc={!deleteMutation.isPending}
        closeOnOverlayClick={!deleteMutation.isPending}
        isOpen={disclosure.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete organization</AlertDialogHeader>
            <AlertDialogBody>
              <Flex direction="column" gap={4}>
                <Text>
                  This permanently deletes <strong>{orgName}</strong>, including
                  its apps, flags, environments, tracked data, memberships, and
                  integrations.
                </Text>
                <Alert alignItems="flex-start" status="warning">
                  <AlertIcon />
                  <Text>
                    This cannot be undone. It does not cancel external Stripe
                    subscriptions or uninstall external Slack or GitHub apps.
                  </Text>
                </Alert>
                <Box>
                  <FormLabel htmlFor="delete-org-confirmation">
                    Type <strong>{orgName}</strong> to confirm
                  </FormLabel>
                  <Input
                    autoComplete="off"
                    id="delete-org-confirmation"
                    isDisabled={deleteMutation.isPending}
                    value={confirmation}
                    onChange={(event) =>
                      setConfirmation(event.currentTarget.value)
                    }
                  />
                </Box>
              </Flex>
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={cancelRef}
                  isDisabled={deleteMutation.isPending}
                  variant="ghostDimmed"
                  onClick={closeDialog}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isDisabled={confirmation !== orgName}
                  isLoading={deleteMutation.isPending}
                  onClick={handleDelete}
                >
                  Permanently delete organization
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

function formatCurrencyFromCents(
  value: number | null,
  currency = "USD",
): string {
  if (value === null) {
    return "—";
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(value / 100);
}

function formatNullableValue(value: number | string | null): string {
  return value === null ? "—" : value.toString();
}

function getAPIErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    return error.response?.data?.error?.message ?? error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}

function AdminStripeBillingSection({ orgId }: { orgId: string }) {
  const toast = useToast();
  const {
    data: billing,
    isLoading,
    error: billingError,
  } = useAdminStripeBilling(orgId);
  const updateOrgMutation = useAdminOrgPatchMutation(orgId);
  const syncMutation = useAdminStripeBillingSyncMutation(orgId);
  const [stripeCustomerIdValue, setStripeCustomerIdValue] = useState("");
  const [stripeSubscriptionIdValue, setStripeSubscriptionIdValue] =
    useState("");
  const [stripeSyncEnabled, setStripeSyncEnabled] = useState(false);

  useEffect(() => {
    if (!billing) {
      return;
    }

    setStripeCustomerIdValue(billing.stripeCustomerId ?? "");
    setStripeSubscriptionIdValue(billing.stripeSubscriptionId ?? "");
    setStripeSyncEnabled(billing.stripeSyncMtuPackagesMonthly);
  }, [billing]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!billing) {
    return (
      <Text color="red.500">
        Failed to load Stripe billing details:{" "}
        {getAPIErrorMessage(billingError, "Unknown error")}
      </Text>
    );
  }

  const hasStripeChanges =
    stripeCustomerIdValue.trim() !== (billing.stripeCustomerId ?? "") ||
    stripeSubscriptionIdValue.trim() !== (billing.stripeSubscriptionId ?? "") ||
    stripeSyncEnabled !== billing.stripeSyncMtuPackagesMonthly;

  const handleSaveStripeSettings = () => {
    updateOrgMutation.mutate(
      {
        stripeCustomerId: stripeCustomerIdValue.trim() || null,
        stripeSubscriptionId: stripeSubscriptionIdValue.trim() || null,
        stripeSyncMtuPackagesMonthly: stripeSyncEnabled,
      },
      {
        onSuccess: () => {
          toast({
            title: "Stripe billing settings updated",
            status: "success",
          });
        },
        onError: (error) => {
          toast({
            title: getAPIErrorMessage(
              error,
              "Failed to update Stripe billing settings",
            ),
            status: "error",
          });
        },
      },
    );
  };

  const handleRunSync = async () => {
    try {
      await syncMutation.mutateAsync();
      toast({
        title: "Stripe MTU sync completed",
        status: "success",
      });
    } catch (error: unknown) {
      toast({
        title: getAPIErrorMessage(error, "Failed to run Stripe MTU sync"),
        status: "error",
      });
    }
  };

  const isSyncActionDisabled = !billing.stripeSubscriptionId?.trim();

  return (
    <section>
      <Heading as="h2" fontSize="2xl" marginBottom={4}>
        Stripe / MTU Billing
      </Heading>

      <Table>
        <colgroup>
          <col width="230" />
        </colgroup>
        <Tbody>
          <Tr>
            <Th>Stripe customer ID</Th>
            <Td>
              <Input
                value={stripeCustomerIdValue}
                onChange={(e) =>
                  setStripeCustomerIdValue(e.currentTarget.value)
                }
              />
            </Td>
            <Td>
              {billing.stripeCustomerDashboardUrl ? (
                <Button
                  as="a"
                  href={billing.stripeCustomerDashboardUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  variant="outline"
                >
                  Open in Stripe
                </Button>
              ) : null}
            </Td>
          </Tr>
          <Tr>
            <Th>Stripe subscription ID</Th>
            <Td>
              <Input
                value={stripeSubscriptionIdValue}
                onChange={(e) =>
                  setStripeSubscriptionIdValue(e.currentTarget.value)
                }
              />
            </Td>
            <Td>
              {billing.stripeSubscriptionDashboardUrl ? (
                <Button
                  as="a"
                  href={billing.stripeSubscriptionDashboardUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  variant="outline"
                >
                  Open in Stripe
                </Button>
              ) : null}
            </Td>
          </Tr>
          <Tr>
            <Th>Stripe billing</Th>
            <Td>
              <Flex alignItems="center" gap={4} wrap="wrap">
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Plan interval:
                  </Text>{" "}
                  {billing.billingInterval ?? "—"}
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Stripe MTU quantity:
                  </Text>{" "}
                  {formatNullableValue(billing.stripePackageQuantity)}
                </Text>
                {billing.billingInterval === "year" ? (
                  <Badge colorScheme="orange">Yearly sync skipped</Badge>
                ) : null}
                {billing.drift ? <Badge colorScheme="red">Drift</Badge> : null}
              </Flex>
            </Td>
            <Td></Td>
          </Tr>
          <Tr>
            <Th>Month MTUs</Th>
            <Td>
              <Flex alignItems="center" gap={4} wrap="wrap">
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Current:
                  </Text>{" "}
                  {billing.currentMonthMtu}
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Previous:
                  </Text>{" "}
                  {billing.previousMonthMtu}
                </Text>
              </Flex>
            </Td>
            <Td></Td>
          </Tr>
          <Tr>
            <Th>Computed package quantity</Th>
            <Td>{billing.syncComputedPackageQuantity}</Td>
            <Td></Td>
          </Tr>
          <Tr>
            <Th>Latest invoice</Th>
            <Td>
              <Flex alignItems="center" gap={3}>
                <Text>{billing.latestInvoiceStatus ?? "—"}</Text>
                {billing.latestInvoiceAmount !== null ? (
                  <Text fontWeight="semibold">
                    {formatCurrencyFromCents(
                      billing.latestInvoiceAmount,
                      billing.latestInvoiceCurrency ?? "USD",
                    )}
                  </Text>
                ) : null}
              </Flex>
            </Td>
            <Td>
              {billing.latestInvoiceUrl ? (
                <Button
                  as="a"
                  href={billing.latestInvoiceUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  variant="outline"
                >
                  Open invoice
                </Button>
              ) : null}
            </Td>
          </Tr>
          <Tr>
            <Th>MTU sync enabled</Th>
            <Td>
              <Switch
                aria-label="Enable monthly MTU sync"
                isChecked={stripeSyncEnabled}
                onChange={(e) => setStripeSyncEnabled(e.currentTarget.checked)}
              />
            </Td>
            <Td>
              {hasStripeChanges ? (
                <Button
                  colorScheme="blue"
                  isLoading={updateOrgMutation.isPending}
                  onClick={handleSaveStripeSettings}
                >
                  Save Stripe settings
                </Button>
              ) : null}
            </Td>
          </Tr>
          <Tr>
            <Th>Last sync</Th>
            <Td>
              <Flex alignItems="center" gap={3}>
                <Text>
                  {billing.stripeMtuLastSyncAt
                    ? new Date(billing.stripeMtuLastSyncAt).toLocaleString()
                    : "—"}
                </Text>
                {billing.stripeMtuLastSyncStatus ? (
                  <Badge
                    colorScheme={
                      billing.stripeMtuLastSyncStatus === "success"
                        ? "green"
                        : billing.stripeMtuLastSyncStatus === "failed"
                          ? "red"
                          : "orange"
                    }
                  >
                    {billing.stripeMtuLastSyncStatus}
                  </Badge>
                ) : null}
              </Flex>
              {billing.stripeMtuLastSyncError ? (
                <Text color="red.500" marginTop={2}>
                  {billing.stripeMtuLastSyncError}
                </Text>
              ) : null}
            </Td>
            <Td>
              <Button
                isDisabled={isSyncActionDisabled}
                isLoading={syncMutation.isPending}
                onClick={handleRunSync}
              >
                Run sync now
              </Button>
            </Td>
          </Tr>
        </Tbody>
      </Table>

      {billing.stripeError ? (
        <Text color="red.500" marginTop={4}>
          Stripe error: {billing.stripeError}
        </Text>
      ) : null}
    </section>
  );
}

function CreateDemoAppModal({
  orgId,
  disclosure,
}: {
  orgId: string;
  disclosure: ReturnType<typeof useDisclosure>;
}) {
  const createMutation = useAdminDemoAppCreateMutation(orgId!);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;

    const values = Object.fromEntries(new FormData(form).entries());

    createMutation.mutate(values as AdminCreateDemoAppType, {
      onSuccess: () => {
        disclosure.onClose();
      },
    });
  };

  return (
    <Modal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      isOpen={disclosure.isOpen}
      onClose={disclosure.onClose}
      onCloseComplete={() => {
        formRef.current?.reset();
      }}
    >
      <ModalOverlay />
      <form ref={formRef} onSubmit={handleSubmit}>
        <ModalContent>
          <ModalHeader>Create new demo app</ModalHeader>
          <ModalBody as={Flex} direction="column" gap={4}>
            <Box>
              <FormLabel htmlFor="demo-app-name">App name</FormLabel>
              <Input
                autoComplete="off"
                defaultValue="Slick"
                id="demo-app-name"
                isDisabled={createMutation.isPending}
                name="name"
                data-1p-ignore
              />
            </Box>

            <Box>
              <FormLabel htmlFor="demo-app-scenario">Scenario</FormLabel>
              <Select
                id="demo-app-scenario"
                isDisabled={createMutation.isPending}
                name="demoScenario"
              >
                {CreateAppScenarios.map((scenario) => (
                  <option key={scenario} value={scenario}>
                    {scenario}
                  </option>
                ))}
              </Select>
            </Box>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                isDisabled={createMutation.isPending}
                type="button"
                variant="outline"
                onClick={disclosure.onClose}
              >
                Cancel
              </Button>
              <Button
                isLoading={createMutation.isPending}
                type="submit"
                variant="solid"
              >
                Create
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
}
